import { networkIconFor } from '@alison/ui';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { formatPercent } from '../../lib/format.js';
import { CategoryDot } from './ElementTag.js';
import { FieldTooltip } from './FieldHeader.js';
import type { Gem, GemGroup } from './gemRows.js';
import { CHECKBOX_SX, Pill } from './Pill.js';
import { formatValue } from './resultRows.js';

export const UPLIFT = 'vs. Account Avg';
export const SPEND = 'Spend';

// A tag can be a whole line of ad copy, so both lines of the card's heading are
// clamped and the full text is a hover away. Cards keep the same height either
// way, which the grid depends on.
export const ONE_LINE = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const LABEL_SX = {
  fontSize: 10,
  fontWeight: 600,
  color: 'text.disabled',
};

export const CARD_SX = {
  gap: 2,
  p: 3,
  borderRadius: 2,
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
};

export const GEM_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
  gap: 2,
};

const MEDIUM_POTENTIAL_SX = (theme: Theme) => ({
  bgcolor: theme.palette.primary.bg,
  color: theme.palette.primary.main,
});

export const UPLIFT_SX = (theme: Theme) => ({
  bgcolor: alpha(theme.palette.success.dark, 0.1),
  color: theme.palette.success.dark,
});

// The service's own words, so an unknown one reads as itself rather than as the
// lowest tier.
const POTENTIAL_SX: Record<string, (theme: Theme) => object> = {
  high: UPLIFT_SX,
  medium: MEDIUM_POTENTIAL_SX,
};

export interface GemPicks {
  picked: string[];
  onToggle: (key: string) => void;
}

export function VsAvgPill({ value }: { value: number | null }): ReactNode {
  if (value === null) return null;
  return <Pill sx={UPLIFT_SX}>↑ {formatPercent(value, 0)} VS AVG.</Pill>;
}

export function SpendShareNote({ share }: { share: number | null }): ReactNode {
  if (share === null) return null;
  return (
    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
      ({formatPercent(share, 1)} of total)
    </Typography>
  );
}

function sublineOf(gem: Gem): string {
  return [gem.scene, `${gem.creativeCount} creative${gem.creativeCount === 1 ? '' : 's'}`]
    .filter((part) => part !== null)
    .join(' · ');
}

export function PotentialPill({ potential }: { potential: string | null }): ReactNode {
  if (potential === null) return null;
  return (
    <Pill sx={POTENTIAL_SX[potential.toLowerCase()] ?? {}}>
      {potential.toUpperCase()} POTENTIAL
    </Pill>
  );
}

export function MetricBlock({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: ReactNode;
}): ReactNode {
  return (
    <Stack sx={{ gap: 0.5, minWidth: 0 }}>
      <Typography sx={LABEL_SX}>{label.toUpperCase()}</Typography>
      <Stack direction="row" sx={{ gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{value}</Typography>
        {note}
      </Stack>
    </Stack>
  );
}

export function ExpandToggle({
  open,
  onToggle,
  sx,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  sx: SxProps<Theme>;
  children: ReactNode;
}): ReactNode {
  return (
    <Stack
      direction="row"
      component="button"
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      sx={[
        {
          border: 0,
          bgcolor: 'transparent',
          cursor: 'pointer',
          alignItems: 'center',
          alignSelf: 'flex-start',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <ExpandMoreOutlined
        sx={{
          fontSize: 18,
          color: 'text.secondary',
          transform: open ? 'none' : 'rotate(-90deg)',
          transition: 'transform 150ms ease',
        }}
      />
      {children}
    </Stack>
  );
}

export function GemCard({
  gem,
  picks,
  sx,
}: {
  gem: Gem;
  picks?: GemPicks;
  sx?: SxProps<Theme>;
}): ReactNode {
  const uplift = gem.metrics.find((metric) => metric.metric === UPLIFT);
  const spend = gem.metrics.find((metric) => metric.metric === SPEND);
  // Whatever the scan was configured around — CTR, ROAS — is the third metric.
  const kpi = gem.metrics.find((metric) => metric.metric !== UPLIFT && metric.metric !== SPEND);
  const NetworkIcon = networkIconFor(gem.network);

  return (
    <Stack data-testid={`gem-${gem.key}`} sx={[CARD_SX, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Stack direction="row" sx={{ gap: 1.5, alignItems: 'flex-start' }}>
        {picks === undefined ? null : (
          <Checkbox
            size="small"
            checked={picks.picked.includes(gem.key)}
            onChange={() => picks.onToggle(gem.key)}
            sx={{ ...CHECKBOX_SX, mt: 0.25 }}
            slotProps={{ input: { 'aria-label': `${gem.feature}: ${gem.tag}` } }}
          />
        )}
        <Stack sx={{ gap: 0.5, flex: 1, minWidth: 0 }}>
          <Tooltip title={gem.tag}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, ...ONE_LINE }}>{gem.tag}</Typography>
          </Tooltip>
          <Stack direction="row" sx={{ gap: 0.75, alignItems: 'center', minWidth: 0 }}>
            {NetworkIcon === undefined ? null : (
              <NetworkIcon
                titleAccess={gem.network ?? undefined}
                sx={{ fontSize: 14, flexShrink: 0 }}
              />
            )}
            <Typography sx={{ fontSize: 12, color: 'text.secondary', ...ONE_LINE }}>
              {gem.feature} · {sublineOf(gem)}
            </Typography>
          </Stack>
        </Stack>
        <PotentialPill potential={gem.potential} />
      </Stack>

      <Stack direction="row" sx={{ gap: 5, flexWrap: 'wrap' }}>
        {kpi === undefined ? null : (
          <MetricBlock
            label={kpi.metric}
            value={formatValue(kpi.value, kpi.format, kpi.currencySymbol ?? '$', kpi.precision)}
            note={<VsAvgPill value={uplift?.value ?? null} />}
          />
        )}
        {spend === undefined ? null : (
          <MetricBlock
            label={spend.metric}
            value={formatValue(
              spend.value,
              spend.format,
              spend.currencySymbol ?? '$',
              spend.precision,
            )}
            note={<SpendShareNote share={gem.spendShare} />}
          />
        )}
      </Stack>
    </Stack>
  );
}

// The one category header every workflow shares: dot, name, the service's
// count line, collapsible.
export function CategorySection({
  category,
  meta,
  info = null,
  children,
}: {
  category: string;
  meta: string;
  info?: ReactNode;
  children: ReactNode;
}): ReactNode {
  const [open, setOpen] = useState(true);
  return (
    <Stack sx={{ gap: 2 }}>
      <ExpandToggle open={open} onToggle={() => setOpen(!open)} sx={{ gap: 1.5, px: 1, py: 0.5 }}>
        <CategoryDot category={category} />
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{category}</Typography>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{meta}</Typography>
        {info}
      </ExpandToggle>
      <Collapse in={open}>{children}</Collapse>
    </Stack>
  );
}

export function GemGroupSection({
  group,
  picks,
  bestTooltip = null,
}: {
  group: GemGroup;
  picks?: GemPicks;
  // The service's explanation of "Best"; absent until it ships, so no icon then.
  bestTooltip?: string | null;
}): ReactNode {
  const count = group.gemCount ?? group.gems.length;

  return (
    <CategorySection
      category={group.category}
      meta={
        `${count} gem${count === 1 ? '' : 's'}` +
        (group.bestUplift === null ? '' : ` · Best ↑ ${formatPercent(group.bestUplift, 0)}`)
      }
      info={group.bestUplift === null ? null : <FieldTooltip tooltip={bestTooltip} />}
    >
      <Box sx={GEM_GRID_SX}>
        {group.gems.map((gem) => (
          <GemCard key={gem.key} gem={gem} {...(picks === undefined ? {} : { picks })} />
        ))}
      </Box>
    </CategorySection>
  );
}
